import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { classForDueDate, titleForDueDate } from '@trello/dates';
import { getStringForCombinedDateBadge } from '@trello/dates/i18n';
import { ClockIcon } from '@trello/nachos/icons/clock';
import type { BadgesTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { Dates } from 'app/scripts/lib/dates';
import type { BadgeProps } from './Badge';
import { Badge } from './Badge';

const DateEvents = Dates as unknown as {
  on: (event: 'renderInterval', callback: (now: number) => void) => void;
  off: (event: 'renderInterval', callback: (now: number) => void) => void;
};

interface DueDateBadgeProps {
  startDate?: Date;
  dueDate?: Date;
  dueComplete?: boolean;
}

export const DueDateBadge: FunctionComponent<DueDateBadgeProps> = ({
  startDate,
  dueDate,
  dueComplete,
}) => {
  const [dueClass, setDueClass] = useState<string | null>(() =>
    dueDate ? classForDueDate(dueDate, dueComplete, Date.now()) : null,
  );

  const updateDueClass = useCallback(() => {
    setDueClass(
      dueDate ? classForDueDate(dueDate, dueComplete, Date.now()) : null,
    );
  }, [dueComplete, dueDate]);

  /**
   * Periodically update the dueClass variable, so we can keep the color of
   * the badge up to date. But avoid listening to this event if the dueDate
   * has not been set or if it has already been marked as complete.
   */
  useEffect(() => {
    // Immediately update the class when the due date or complete fields change.
    updateDueClass();

    if (dueDate && !dueComplete) {
      DateEvents.on('renderInterval', updateDueClass);
      return () => DateEvents.off('renderInterval', updateDueClass);
    }
  }, [dueComplete, dueDate, updateDueClass]);

  const badgeColorProps: Pick<BadgeProps, 'color' | 'isBold'> | null =
    useMemo(() => {
      if (!dueDate) {
        return null;
      }
      if (dueComplete) {
        return { color: 'green', isBold: true };
      }

      switch (dueClass) {
        case 'is-due-soon':
          return { color: 'yellow', isBold: true };
        case 'is-due-past':
          return { color: 'red' };
        case 'is-due-now':
          return { color: 'red', isBold: true };
        default:
          return null;
      }
    }, [dueDate, dueComplete, dueClass]);

  return (
    <Badge
      icon={<ClockIcon />}
      title={dueDate ? titleForDueDate(dueDate, dueComplete) : undefined}
      testId={
        dueComplete
          ? getTestId<BadgesTestIds>('badge-due-date-completed')
          : getTestId<BadgesTestIds>('badge-due-date-not-completed')
      }
      {...badgeColorProps}
    >
      {getStringForCombinedDateBadge(startDate, dueDate)}
    </Badge>
  );
};

interface DueDateBadgeWrapperProps {
  due?: string | null;
  start?: string | null;
  dueComplete?: boolean;
  isTemplate?: boolean;
}

// Thin layer around DueDateBadge to avoid excessive rendering.
export const DueDateBadgeWrapper: FunctionComponent<
  DueDateBadgeWrapperProps
> = ({ due, start, dueComplete, isTemplate }) => {
  if ((!due && !start) || isTemplate) {
    return null;
  }
  return (
    <DueDateBadge
      startDate={start ? new Date(start) : undefined}
      dueDate={due ? new Date(due) : undefined}
      dueComplete={dueComplete}
    />
  );
};
